import { AbstractTagClass } from 'tag/ts/tag/abstract/AbstractTagClass';
import { ITagAPINerdMode } from 'tag/ts/tag/interfaces/api/ITagAPINerdMode';
import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { INerdModeContainerTab } from 'tag/ts/tag/interfaces/nerdmode/INerdModeContainerTab';
import { INerdModeGetLogOptions } from 'tag/ts/tag/interfaces/nerdmode/INerdModeGetLogOptions';
import { INerdModeLiveTab } from 'tag/ts/tag/interfaces/nerdmode/INerdModeLiveTab';
import { INerdModeLogSnapshot } from 'tag/ts/tag/interfaces/nerdmode/INerdModeLogSnapshot';
import { INerdModeManager } from 'tag/ts/tag/interfaces/nerdmode/INerdModeManager';
import { INerdModeTab } from 'tag/ts/tag/interfaces/nerdmode/INerdModeTab';

/**
 * @copyright © Paramount Streaming
 * @author Stan Gletski
 * @description Backs `tag.API.TAG.NerdMode` — extracted out of `TagAPI` once the namespace
 * grew to 27 methods. Every method resolves the manager via `#getNerdModeManager()` and
 * no-ops (or returns a safe default) when Nerd Mode isn't enabled/initialized yet.
 *
 * Methods are arrow class-fields (not prototype methods) so `this` stays bound if a host
 * destructures a method off the namespace, e.g. `const { show } = tag.API.TAG.NerdMode`.
 * `TagAPI.IN.NerdMode` is just a reference to this instance, constructed once in `TagAPI`'s
 * constructor rather than a fresh object literal rebuilt on every `.IN` access.
 *
 * See {@link ITagAPINerdMode} for full per-method documentation.
 */
export class TagNerdModeAPI extends AbstractTagClass implements ITagAPINerdMode {

	static readonly #DEFAULT_SCROLL_PIXELS = 40;

	constructor( argument: ITagAbstractClassArgument ) {
		super(
			{
				...argument,
				name: 'TagNerdModeAPI'
			}
		);
	}

	/**
	 * @description Shorthand for the full call chain down to the Nerd Mode manager. Returns
	 * null at any broken link (no controller, no player manager, no adapter, or the adapter
	 * doesn't implement Nerd Mode).
	 */
	#getNerdModeManager(): INerdModeManager | null {
		return this.tag.controller?.playerManager?.playerAdapter?.getNerdModeManager?.() ?? null;
	}

	show = (): void => {
		this?.logger?.log( 'IN:', 'NerdMode.show' );
		this.#getNerdModeManager()?.show();
	};

	hide = (): void => {
		this?.logger?.log( 'IN:', 'NerdMode.hide' );
		this.#getNerdModeManager()?.hide();
	};

	toggle = (): void => {
		this?.logger?.log( 'IN:', 'NerdMode.toggle' );
		this.#getNerdModeManager()?.toggle();
	};

	isVisible = (): boolean => {
		return this.#getNerdModeManager()?.isVisible ?? false;
	};

	toggleVerbose = (): void => {
		this?.logger?.log( 'IN:', 'NerdMode.toggleVerbose' );
		this.#getNerdModeManager()?.toggleVerbose();
	};

	setVerbose = ( enabled: boolean ): void => {
		this?.logger?.log( 'IN:', `NerdMode.setVerbose(${enabled})` );
		this.#getNerdModeManager()?.setVerbose( enabled );
	};

	getVerbose = (): boolean => {
		return this.#getNerdModeManager()?.getVerbose() ?? false;
	};

	setExpandAllNodes = ( enabled: boolean ): void => {
		this?.logger?.log( 'IN:', `NerdMode.setExpandAllNodes(${enabled})` );
		this.#getNerdModeManager()?.setExpandAllNodes( enabled );
	};

	getExpandAllNodes = (): boolean => {
		return this.#getNerdModeManager()?.getExpandAllNodes() ?? false;
	};

	toggleDetached = (): void => {
		this?.logger?.log( 'IN:', 'NerdMode.toggleDetached' );
		this.#getNerdModeManager()?.toggleDetached();
	};

	getDetached = (): boolean => {
		return this.#getNerdModeManager()?.getDetached() ?? false;
	};

	scrollUp = ( pixels?: number ): void => {
		const scrollAmount = pixels ?? TagNerdModeAPI.#DEFAULT_SCROLL_PIXELS;
		this?.logger?.log( 'IN:', `NerdMode.scrollUp(${scrollAmount})` );
		this.#getNerdModeManager()?.scrollUp( pixels );
	};

	scrollDown = ( pixels?: number ): void => {
		const scrollAmount = pixels ?? TagNerdModeAPI.#DEFAULT_SCROLL_PIXELS;
		this?.logger?.log( 'IN:', `NerdMode.scrollDown(${scrollAmount})` );
		this.#getNerdModeManager()?.scrollDown( pixels );
	};

	scrollToTop = (): void => {
		this?.logger?.log( 'IN:', 'NerdMode.scrollToTop' );
		this.#getNerdModeManager()?.scrollToTop();
	};

	scrollToBottom = (): void => {
		this?.logger?.log( 'IN:', 'NerdMode.scrollToBottom' );
		this.#getNerdModeManager()?.scrollToBottom();
	};

	pageUp = (): void => {
		this?.logger?.log( 'IN:', 'NerdMode.pageUp' );
		this.#getNerdModeManager()?.pageUp();
	};

	pageDown = (): void => {
		this?.logger?.log( 'IN:', 'NerdMode.pageDown' );
		this.#getNerdModeManager()?.pageDown();
	};

	getScrollInfo = (): { scrollTop: number; scrollHeight: number; clientHeight: number; isAtTop: boolean; isAtBottom: boolean } | null => {
		return this.#getNerdModeManager()?.getScrollInfo() ?? null;
	};

	registerTab = ( tab: INerdModeTab ): INerdModeLiveTab => {
		this?.logger?.log( 'IN:', `NerdMode.registerTab("${tab?.label}")` );
		return this.#getNerdModeManager()?.registerTab( tab )
			?? {
				label: tab.label, index: -1, isBuiltIn: false, focus: () => {
				}, remove: () => {
				}, scrollUp: () => {
				}, scrollDown: () => {
				}, scrollToTop: () => {
				}, scrollToBottom: () => {
				}, pageUp: () => {
				}, pageDown: () => {
				}, getScrollInfo: () => null
			};
	};

	registerContainerTab = ( tab: INerdModeContainerTab ): INerdModeLiveTab => {
		this?.logger?.log( 'IN:', `NerdMode.registerContainerTab("${tab?.label}")` );
		return this.#getNerdModeManager()?.registerContainerTab( tab )
			?? {
				label: tab.label, index: -1, isBuiltIn: false, focus: () => {
				}, remove: () => {
				}, scrollUp: () => {
				}, scrollDown: () => {
				}, scrollToTop: () => {
				}, scrollToBottom: () => {
				}, pageUp: () => {
				}, pageDown: () => {
				}, getScrollInfo: () => null
			};
	};

	getTabs = (): INerdModeLiveTab[] => {
		return this.#getNerdModeManager()?.getTabs() ?? [];
	};

	focusPlayerTab = (): void => {
		this?.logger?.log( 'IN:', 'NerdMode.focusPlayerTab' );
		this.#getNerdModeManager()?.focusPlayerTab();
	};

	nextTab = (): void => {
		this?.logger?.log( 'IN:', 'NerdMode.nextTab' );
		this.#getNerdModeManager()?.nextTab();
	};

	previousTab = (): void => {
		this?.logger?.log( 'IN:', 'NerdMode.previousTab' );
		this.#getNerdModeManager()?.previousTab();
	};

	focusByIndex = ( index: number ): boolean => {
		this?.logger?.log( 'IN:', `NerdMode.focusByIndex(${index})` );
		return this.#getNerdModeManager()?.focusByIndex( index ) ?? false;
	};

	focusByLabel = ( label: string ): boolean => {
		this?.logger?.log( 'IN:', `NerdMode.focusByLabel("${label}")` );
		return this.#getNerdModeManager()?.focusByLabel( label ) ?? false;
	};

	getLog = ( options?: INerdModeGetLogOptions ): INerdModeLogSnapshot | null => {
		return this.#getNerdModeManager()?.getLog( options ) ?? null;
	};

	startCapture = (): void => {
		this?.logger?.log( 'IN:', 'NerdMode.startCapture' );
		this.#getNerdModeManager()?.startCapture();
	};

	stopCapture = (): void => {
		this?.logger?.log( 'IN:', 'NerdMode.stopCapture' );
		this.#getNerdModeManager()?.stopCapture();
	};

}